<template>
	<div>
		<div v-if="loading > 0">
			Loading...
		</div>
		<div v-else-if="user">
			<h2>Billing</h2>

			<h3>Current plan</h3>
			<b-card class="mb-3">
				<div class="d-flex justify-content-between align-items-stretch">
					<div v-if="user.is_subscribed">
						<h4>{{ planNames[user.plan] || user.plan }}</h4>
						<p class="mb-1"><span :class="{ 'text-strike': user.subscription_status == 'gratuity' }"><b>${{ planPrices[user.period] && planPrices[user.period][user.plan] }}</b>/{{ user.period === 'monthly' ? 'mo' : 'yr' }}</span> <b-badge variant="success" v-if="user.subscription_status == 'gratuity'">gratuity</b-badge></p>
						<ul class="pl-3 mb-3">
							<li v-for="feature in (planFeatures[user.plan] || [])" :key="feature">{{ feature }}</li>
						</ul>
						<b-skeleton v-if="upcomingInvoiceLoading" width="220px" class="mt-2" />
						<template v-else-if="user.subscription_status === 'trialing'">
							<b-badge variant="info" class="mb-2">Free trial</b-badge>
							<p v-if="upcomingInvoice" class="text-muted mb-1">Trial ends on {{ upcomingInvoice.date }}.</p>
							<b-button size="sm" variant="primary" :disabled="portalLoading" @click="managePortal()">
								<b-spinner v-if="portalLoading" small class="mr-1" />
								Add payment method
							</b-button>
						</template>
						<p v-else-if="upcomingInvoice" class="text-muted mb-0">Next payment: <b>{{ upcomingInvoice.amount }}</b> on {{ upcomingInvoice.date }}</p>
						<b-alert v-else variant="danger" :show="!!upcomingInvoiceError" class="mb-0">Could not load next payment: {{ upcomingInvoiceError }}</b-alert>
					</div>
					<p v-else class="text-muted mb-0">No active subscription.</p>
					<div class="ml-3 d-flex flex-column align-items-end flex-shrink-0">
						<b-button @click="$root.$emit('show-upgrade-modal')" variant="success">{{user.is_pro ? 'Change plan' : 'Upgrade'}}</b-button>
						<b-button v-if="user.is_subscribed && user.subscription_status !== 'gratuity'" variant="danger" size="sm" class="mt-auto" @click="$refs.cancelModal.show()">Cancel subscription</b-button>
					</div>
				</div>
			</b-card>

			<h3>Billing management</h3>
			<b-card class="mb-3">
				<div class="d-flex justify-content-between align-items-center">
					<div>
						<p class="text-muted mb-2">Manage your payment methods, billing address, and subscription in Stripe.</p>
						<b-button :disabled="portalLoading" @click="managePortal()">
							<b-spinner v-if="portalLoading" small class="mr-1" />
							Manage billing
						</b-button>
					</div>
					<img src="/Stripe%20wordmark%20-%20White.svg" alt="Stripe" height="32" class="ml-3" />
				</div>
			</b-card>

			<h3>Invoices</h3>
			<b-skeleton-table v-if="invoicesLoading" :rows="3" :columns="4" :table-props="{ borderless: true, striped: true }" class="mb-3" />
			<template v-else>
				<b-alert variant="danger" :show="!!invoicesError" class="mb-3">Could not load invoices: {{ invoicesError }}</b-alert>
				<p v-if="!invoicesError && invoices.length === 0" class="text-muted">No invoices yet.</p>
				<b-table v-if="invoices.length > 0" :items="invoices" :fields="invoiceFields" striped borderless no-border-collapse class="mb-3">
				<template #cell(links)="{ item }">
					<b-link v-if="item.url" :href="item.url" target="_blank">View</b-link>
					<b-link v-if="item.pdf" :href="item.pdf" target="_blank" class="ml-2" title="Download PDF">
						<b-icon icon="file-earmark-pdf" /> PDF
					</b-link>
				</template>
			</b-table>
			</template>
		</div>
		<div v-else>
			No user data.
		</div>

		<account-cancel-modal ref="cancelModal" @cancelled="loadBillingData"></account-cancel-modal>
	</div>
</template>

<script>
import AccountCancelModal from '@/components/Account/Modal/Cancel';

export default {
	components: {
		AccountCancelModal,
	},
	data: () => ({
		loading: 0,
		portalLoading: false,
		error: null,
		user: null,
		invoices: [],
		invoicesLoading: false,
		invoicesError: null,
		upcomingInvoice: null,
		upcomingInvoiceLoading: false,
		upcomingInvoiceError: null,
		invoiceFields: [
			{ key: 'date', label: 'Date' },
			{ key: 'amount', label: 'Amount' },
			{ key: 'status', label: 'Status' },
			{ key: 'links', label: '' },
		],
		planNames: { regular: 'Regular', pro: 'Pro' },
		planFeatures: {
			free: [
				'Max 3 channels',
				'Widgets: Chat, Viewer count, Gift alert, Highlighted message'
			],
			regular: [
				'Unlimited channels',
				'Widgets: Chat, Viewer count, Gift alert, Highlighted message',
				'Over 290 voices',
			],
			pro: [
				'Unlimited channels',
				'All widgets from Regular plus song requests',
				'Over 1300 voices',
				'Experimental restreaming',
			],
		},
		planPrices: {
			monthly: { regular: 4, pro: 8 },
			yearly: { regular: 40, pro: 80 },
		},
	}),
	mounted() {
		const params = new Proxy(new URLSearchParams(window.location.search), {
			get: (searchParams, prop) => searchParams.get(prop),
		});

		this.loadBillingData().then(user => {
			this.subId = this.$socket.subscribe(user.widget_hash, {
				userChanged: msg => this.user = { ...this.user, ...msg },
			});

			if(params.checkout === 'success') {
				this.$bvToast.toast('Payment succeeded! Thank you for your purchase.', {
					title: 'Success',
					variant: 'success',
					solid: true,
				});
				window.history.replaceState({}, '', window.location.pathname);
			}
		});
	},
	beforeDestroy() {
		this.subId && this.$socket.unsubscribe(this.subId);
	},
	methods: {
		setUserBillingData(data) {
			this.user = data;
			this.user.is_subscribed = this.user.has_subscription_id && (this.user.subscription_status === 'active' || this.user.subscription_status === 'trialing') || this.user.subscription_status === 'gratuity';
			this.user.is_monthly = this.user.is_subscribed && this.user.period === 'monthly';
			this.user.is_yearly = this.user.is_subscribed && this.user.period === 'yearly';
			this.user.is_regular = this.user.is_subscribed && this.user.plan === 'regular';
			this.user.is_pro = this.user.is_subscribed && this.user.plan === 'pro';
		},
		loadBillingData() {
			this.loading++;
			return this.$api.get('userData').then(user => {
				this.setUserBillingData(user);
				return user;
			}, err => this.error = err).finally(() => this.loading--);
		},
		loadInvoices() {
			this.invoicesLoading = true;
			this.$api.get('invoices').then(invoices => {
				this.invoices = invoices;
			}).catch(e => this.invoicesError = e.message || String(e)).finally(() => this.invoicesLoading = false);
			this.upcomingInvoiceLoading = true;
			this.$api.get('upcomingInvoice').then(inv => {
				this.upcomingInvoice = inv;
			}).catch(e => this.upcomingInvoiceError = e.message || String(e)).finally(() => this.upcomingInvoiceLoading = false);
		},
		async managePortal() {
			this.portalLoading = true;
			try {
				const { url } = await this.$api.post('customerPortal', { returnUrl: window.location.href });
				window.location.href = url;
			} catch(e) {
				this.$bvToast.toast(e.message || String(e), { title: 'Error', variant: 'danger', solid: true });
			} finally {
				this.portalLoading = false;
			}
		},
	},
	watch: {
		user(val) {
			if(val && val.has_customer_id && !this.invoices.length)
				this.loadInvoices();
		},
	},
}
</script>
